import { NavLink, useParams, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, FolderKanban, FilePlus, FolderOpen, LibraryBig,
  BarChart3, Scale, Gavel, Activity, Gauge, MessageCircle, FileText,
  ChevronsLeft, ChevronsRight, HelpCircle, Settings, PieChart,
  Presentation, Images, Link2,
} from "lucide-react";
import Logo from "@/components/Logo";
import { cn } from "@/lib/utils";

export default function Sidebar() {
  const { projectId } = useParams();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const globalNav = [
    { to: "/app/projects", icon: FolderKanban, label: "Mes Projets" },
    { to: "/app/projects/new", icon: FilePlus, label: "Nouveau Projet" },
  ];

  const projectNav = projectId
    ? [
        { to: `/app/projects/${projectId}`, icon: LayoutDashboard, label: "Dashboard", end: true },
        { to: `/app/projects/${projectId}/documents`, icon: LibraryBig, label: "Bibliothèque" },
        { to: `/app/projects/${projectId}/analyses/macro`, icon: BarChart3, label: "Macro-financière" },
        { to: `/app/projects/${projectId}/analyses/vitaliste`, icon: Scale, label: "Vitaliste" },
        { to: `/app/projects/${projectId}/analyses/clauses`, icon: Gavel, label: "Conventions" },
        { to: `/app/projects/${projectId}/analyses/odd`, icon: Activity, label: "Impact ODD" },
        { to: `/app/projects/${projectId}/analyses/legal`, icon: FileText, label: "Analyse Juridique" },
        { to: `/app/projects/${projectId}/simulator`, icon: Gauge, label: "Simulateur" },
        { to: `/app/projects/${projectId}/qa`, icon: MessageCircle, label: "Requête Libre" },
        { to: `/app/projects/${projectId}/reports`, icon: PieChart, label: "Rapports" },
        { to: `/app/projects/${projectId}/infographics`, icon: Images, label: "Infographies" },
        { to: `/app/projects/${projectId}/present`, icon: Presentation, label: "Présentation" },
        { to: `/app/projects/${projectId}/vitae`, icon: Link2, label: "VITAE-PUBLICA" },
      ]
    : [];

  return (
    <aside
      className={cn(
        "sticky top-0 h-screen shrink-0 border-r border-border bg-card flex flex-col transition-[width] duration-200",
        collapsed ? "w-[68px]" : "w-[248px]",
      )}
      data-testid="app-sidebar"
    >
      <div className={cn("flex items-center border-b border-border h-16 px-4", collapsed && "justify-center px-2")}>
        {collapsed ? <Logo size={32} withText={false} /> : <Logo size={34} />}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 space-y-6">
        <Section title={!collapsed && "GLOBAL"} collapsed={collapsed}>
          {globalNav.map((n) => <NavItem key={n.to} {...n} collapsed={collapsed} />)}
        </Section>

        {projectId && (
          <Section title={!collapsed && "PROJET ACTIF"} collapsed={collapsed}>
            {projectNav.map((n) => <NavItem key={n.to} {...n} collapsed={collapsed} />)}
          </Section>
        )}
      </nav>

      <div className="border-t border-border p-2">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center gap-3 rounded-sm px-3 py-2 text-sm text-muted-foreground hover:bg-accent/40 transition"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          {!collapsed && <span className="label-mono text-[10px]">Replier</span>}
        </button>
      </div>
    </aside>
  );
}

function Section({ title, collapsed, children }) {
  return (
    <div>
      {title && <div className="px-5 mb-2 label-mono text-[9px] text-muted-foreground">{title}</div>}
      <div className={cn("px-2 space-y-0.5", collapsed && "px-2")}>{children}</div>
    </div>
  );
}

function NavItem({ to, icon: Icon, label, collapsed, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition hover-lift",
          isActive
            ? "bg-marine/10 text-marine dark:bg-gold/10 dark:text-gold border-l-2 border-marine dark:border-gold"
            : "text-foreground/70 hover:bg-accent/30 hover:text-foreground border-l-2 border-transparent",
          collapsed && "justify-center px-2",
        )
      }
    >
      <Icon size={17} strokeWidth={1.8} />
      {!collapsed && <span className="truncate">{label}</span>}
    </NavLink>
  );
}
